import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  EnvironmentProviders,
  ErrorHandler,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import { Router } from '@angular/router';
import { Sentry, SENTRY } from '@core/const';
import {
  browserTracingIntegration,
  captureException,
  createErrorHandler,
  getCurrentScope,
  init,
  setTag,
  TraceService,
  withScope,
} from '@sentry/angular';
import { ErrorEvent, TransactionEvent } from '@sentry/core';
import { getRoute } from '@shared/utils';
import { SentryErrorHandler, SentryErrorInterceptor } from '../error-handlers';
import { isErrorExcluded } from '../filters';
import { config } from './sentry.config';

export function sentryBrowserInit(): void {
  init({
    ...config,
    integrations: [browserTracingIntegration()],
    beforeSendTransaction: (event: TransactionEvent): TransactionEvent | null => {
      if (event.request && event.request.url) {
        event.transaction = `CSR ${getRoute(event.request.url)}`;
      }

      return event;
    },
    beforeSend: (event: ErrorEvent): ErrorEvent | null => {
      if (event.request && event.request.url) {
        event.transaction = `CSR ${getRoute(event.request.url)}`;
      }

      if (isErrorExcluded(event)) {
        return null;
      }

      return event;
    },
  });
  setTag('ssr', false);
}

export function withSentryBrowserProviders(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: SENTRY,
      useFactory(): Sentry {
        const errorHandler = createErrorHandler({
          showDialog: false,
        });

        const captureUnhandledException = (error: unknown): void => {
          errorHandler.handleError(error);
        };

        return {
          getCurrentScope,
          withScope,
          captureException,
          captureUnhandledException,
        };
      },
    },
    {
      provide: ErrorHandler,
      useExisting: SentryErrorHandler,
    },
    {
      provide: TraceService,
      useClass: TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(TraceService);
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: SentryErrorInterceptor,
      multi: true,
    },
  ]);
}
